import S from 's-js';
import { lift } from 's-array';

import { CachedModelLoader } from 'lib/CachedModelLoader';

import { EnhancedEstimateQueryStatus } from 'server/enums/EnhancedEstimateQuery';

import { AutomationSystemType } from 'data/models/AutomationSystemType';
import { PortCall } from 'data/models/PortCall';
import { WartsilaEnrichmentsResponse, WartsilaEnrichmentsResponseOptions } from 'data/models/WartsilaEnrichmentsResponse';

import { PCCache } from 'data/PCCache';

import { WartsilaEnrichmentsEstimateDetails, WartsilaEnrichmentsEstimateDetailsParams } from './estimateDetails';
import { WartsilaEnrichmentsMap, WartsilaEnrichmentsMapParams } from './map';

export interface WartsilaEnrichmentsSummaryCardParams {
    portcall : PortCall;
    readyForInitialRefresh : PromiseLike<any>;
}

export type WartsilaEnrichmentsSummaryCard = ReturnType<typeof WartsilaEnrichmentsSummaryCard>;
export function WartsilaEnrichmentsSummaryCard(
    loader : CachedModelLoader<WartsilaEnrichmentsResponse, WartsilaEnrichmentsResponseOptions>,
    data : PCCache,
    estimateDetailsModel : (params : WartsilaEnrichmentsEstimateDetailsParams) => WartsilaEnrichmentsEstimateDetails,
    mapModel : (params : WartsilaEnrichmentsMapParams) => WartsilaEnrichmentsMap,
    { portcall, readyForInitialRefresh } : WartsilaEnrichmentsSummaryCardParams
) {
    const
        // All WartsilaEnhancedEstimates automations for this domain
        automations = lift(() => Array.from(data.AutomationSystems.items()))
            .filter(sys =>
                sys.Type() === AutomationSystemType.WartsilaEnhancedEstimates &&
                sys.Active()
            ),
        // Having card content is conditional on having a WartsilaEnhancedEstimates automation configured
        content = S(() => automations().length ? SummaryCardContent(loader, estimateDetailsModel, mapModel, portcall, readyForInitialRefresh) : null);

    return {
        Content: content
    };
}

export type SummaryCardContent = ReturnType<typeof SummaryCardContent>;
function SummaryCardContent(
    loader : CachedModelLoader<WartsilaEnrichmentsResponse, WartsilaEnrichmentsResponseOptions>,
    estimateDetailsModel : (params : WartsilaEnrichmentsEstimateDetailsParams) => WartsilaEnrichmentsEstimateDetails,
    mapModel : (params : WartsilaEnrichmentsMapParams) => WartsilaEnrichmentsMap,
    portcall : PortCall,
    readyForInitialRefresh : PromiseLike<any>
) {
    const { model, loading: modelLoading, task } = loader({ Id: portcall.Id() }, 120),
        estimateDetails = estimateDetailsModel({
            model,
            // Ready for initial refresh when calling code says we're ready and we've loaded our model
            readyForInitialRefresh: (async () => {
                try {
                    await readyForInitialRefresh;
                } finally {
                    await task;
                }
            })()
        }),
        map = mapModel({ model });

    return {
        Id: portcall.Id(),
        HasSuccessfulQuery: () => !!model.RelevantQuery() && model.RelevantQuery()!.Status() === EnhancedEstimateQueryStatus.Succeeded,
        EstimateDetails: estimateDetails,
        Map: map,
        Loading: () => modelLoading() || estimateDetails.AnyRefreshPendingOrInProgress()
    };
}



// WEBPACK FOOTER //
// ./apps/wartsila/wartsilaenrichments/summaryCard.ts